import { PickupDeliveryConsequences } from "./pickup-delivery-consequences";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CreditCard, FileText, Wallet } from "lucide-react";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "@heroui/react";
import {
  CheckBox,
  RadioDot,
  SelectableCard,
  SheetSection,
} from "./sheet-primitives";
import { useToast } from "@/hooks/use-toast";
import {
  PickupEligibilityNotice,
  PickupUnavailableShipments,
} from "./pickup-eligibility-notice";
import { usePickupSelectionRecovery } from "./use-pickup-selection-recovery";
import {
  useMyPaymentTerms,
  usePaymentMethods,
} from "@/api/use-payment-methods-api";
import { useCreditBalance } from "@/api/use-payment-options-api";
import {
  isPickupBookingError,
  useAttachShipmentsToBooking,
  useEligiblePickupShipments,
  usePreviewPickupBooking,
  type EligibleShipment,
  type PickupPreview,
} from "@/api/use-schedule-pickup";
import {
  useMyPickupBooking,
  type MyPickupBooking,
} from "@/api/use-my-pickup-bookings";
import { useTranslation } from "react-i18next";

/**
 * Phase 16c: attach additional shipments to an existing pickup booking.
 *
 * Both entry points (B: pickup detail's "Add shipment", C: shipment row's
 * "Attach to pickup") open this same modal. C passes `preselectedShipmentIds`
 * with the shipment the user clicked from — the user can still uncheck it.
 */
const TERMINAL_PICKUP_ERRORS = new Set([
  "BOOKING_CANCELLED",
  "BOOKING_IN_PROGRESS",
  "BOOKING_IN_PAST",
  "BOOKING_NOT_ATTACHABLE",
  "BOOKING_NOT_FOUND",
  "SLOT_CLOSED",
  "SLOT_FULL",
  "PAST_DATE",
]);

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  booking: MyPickupBooking;
  preselectedShipmentIds?: string[];
  onScheduleNew?: (shipmentIds: string[]) => void;
}

const numberFmt = (cents: number, currency = "CAD", locale = "en-CA") =>
  new Intl.NumberFormat(locale, { style: "currency", currency }).format(
    cents / 100
  );

type PickupPaymentChoice = "SAVED_CARD" | "CREDIT" | "INVOICE";

function generateIdempotencyKey(): string {
  return crypto.randomUUID();
}

function formatSlotRange(
  slot: { startTime: string; endTime: string },
  locale: string
): string {
  const fmt = (t: string) => {
    const [h, m] = t.split(":").map(Number);
    if (locale.toLowerCase().startsWith("zh")) {
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    }
    const period = h >= 12 ? "PM" : "AM";
    const dh = h % 12 || 12;
    return m === 0
      ? `${dh}${period}`
      : `${dh}:${m.toString().padStart(2, "0")}${period}`;
  };
  return `${fmt(slot.startTime)} – ${fmt(slot.endTime)}`;
}

export function AttachToBookingModal({
  open,
  onClose,
  onSuccess,
  booking: initialBooking,
  preselectedShipmentIds,
  onScheduleNew,
}: Props) {
  const { toast } = useToast();
  const { t, i18n } = useTranslation("app");
  const locale = i18n.resolvedLanguage ?? i18n.language;

  const bookingQuery = useMyPickupBooking(open ? initialBooking.id : null);
  const booking = bookingQuery.data ?? initialBooking;
  const bookingSlotId = booking.slotId ?? booking.slot?.id;
  const [blocked, setBlocked] = useState(false);
  const [refreshingBooking, setRefreshingBooking] = useState(false);
  const targetUnavailable =
    !!booking.cancelledAt || booking.derivedState !== "Scheduled";
  const bookingAddressId = booking.pickupAddress.id;

  const [selectedShipmentIds, setSelectedShipmentIds] = useState<Set<string>>(
    () => new Set(preselectedShipmentIds ?? [])
  );
  const [paymentChoice, setPaymentChoice] = useState<PickupPaymentChoice | "">(
    ""
  );
  const [paymentMethodId, setPaymentMethodId] = useState<string>("");
  const submission = useRef<{ identity: string; key: string } | null>(null);

  const [reviewRevision, setReviewRevision] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: eligibleShipments, isLoading: loadingShipments } =
    useEligiblePickupShipments();
  const { data: paymentMethodsData } = usePaymentMethods();
  const paymentMethods = paymentMethodsData?.paymentMethods ?? [];
  const { data: creditBalance } = useCreditBalance();
  const { data: paymentTermsData } = useMyPaymentTerms();
  const previewMutation = usePreviewPickupBooking();
  const quoteKey = JSON.stringify([
    booking.id,
    bookingAddressId,
    booking.pickupService.id,
    bookingSlotId,
    booking.scheduledDate,
    booking.timingAvailability,
    booking.slot,
    booking.timeZone,
    booking.schedulingTimeZone,
    booking.dispatchCenter.timeZone,
    booking.cancelledAt,
    booking.derivedState,
    booking.notes,
    booking.contactSnapshotAt,
    booking.contactName,
    booking.contactCompany,
    booking.contactPhone,
    booking.contactEmail,
    booking.instructions,
    booking.isResidential,
    booking.shipments.map((shipment) => [shipment.id, shipment.status]),
    [...selectedShipmentIds].sort(),
  ]);
  const [quote, setQuote] = useState<{
    key: string;
    data: PickupPreview;
  } | null>(null);
  const preview = quote?.key === quoteKey ? quote.data : null;
  const visitContact =
    preview?.pickupContact ??
    (booking.contactSnapshotAt
      ? {
          contactName: booking.contactName,
          contactCompany: booking.contactCompany,
          contactPhone: booking.contactPhone,
          contactEmail: booking.contactEmail,
          isResidential: booking.isResidential,
          instructions: booking.instructions,
        }
      : null);
  const setPreview = useCallback(
    (data: PickupPreview | null) => {
      setQuote(data ? { key: quoteKey, data } : null);
    },
    [quoteKey]
  );

  const visibleShipments: EligibleShipment[] = useMemo(() => {
    if (!eligibleShipments) return [];
    const attachedIds = new Set(booking.shipments.map((s) => s.id));
    return eligibleShipments.filter((s) => !attachedIds.has(s.id));
  }, [eligibleShipments, booking.shipments]);
  const recovery = usePickupSelectionRecovery(
    eligibleShipments ? visibleShipments : undefined,
    selectedShipmentIds,
    setSelectedShipmentIds
  );

  const availableShipments = visibleShipments.filter(
    (s) => s.pickupEligibility?.eligible !== false
  );
  const unavailableShipments = visibleShipments.filter(
    (s) => s.pickupEligibility?.eligible === false
  );

  const selectedShipments = useMemo(
    () => visibleShipments.filter((s) => selectedShipmentIds.has(s.id)),
    [visibleShipments, selectedShipmentIds]
  );
  const pickupTotalCents = preview ? preview.totalCents + preview.taxCents : 0;
  const canUseCredit =
    !!creditBalance &&
    !creditBalance.frozen &&
    pickupTotalCents > 0 &&
    creditBalance.balanceCents >= pickupTotalCents;
  const selectedOrdersUseNetTerms =
    selectedShipments.length > 0 &&
    selectedShipments.every(
      (s) =>
        s.order.paymentMethodType === "INVOICE" &&
        !!s.order.paymentTermsId &&
        !!s.order.paymentTermsRef &&
        s.order.invoicePending
    );
  const selectedTermNames = Array.from(
    new Set(
      selectedShipments
        .map((s) => s.order.paymentTermsRef?.name)
        .filter((name): name is string => !!name)
    )
  );
  const canUseNetTerms =
    !!paymentTermsData &&
    selectedOrdersUseNetTerms &&
    pickupTotalCents > 0 &&
    !paymentTermsData.hasUnpaidInvoices &&
    paymentTermsData.availableCredit >= pickupTotalCents;
  const paymentReady =
    paymentChoice === "SAVED_CARD"
      ? !!paymentMethodId
      : paymentChoice === "CREDIT"
        ? canUseCredit
        : paymentChoice === "INVOICE"
          ? canUseNetTerms
          : false;
  const netTermsUnavailableReason = !selectedOrdersUseNetTerms
    ? t("pickup.confirm.netTermsRequiresSource")
    : paymentTermsData?.hasUnpaidInvoices
      ? t("pickup.confirm.netTermsBlockedInvoices")
      : paymentTermsData && paymentTermsData.availableCredit < pickupTotalCents
        ? t("pickup.confirm.netTermsInsufficientCredit")
        : null;

  useEffect(() => {
    if (paymentMethodId) return;
    const def = paymentMethods.find((p) => p.isDefault) ?? paymentMethods[0];
    if (def) {
      setPaymentMethodId(def.id);
      if (paymentChoice === "") setPaymentChoice("SAVED_CARD");
    }
  }, [paymentMethods, paymentMethodId, paymentChoice]);

  useEffect(() => {
    previewMutation.reset();
    setPreview(null);
    if (
      !open ||
      targetUnavailable ||
      blocked ||
      refreshingBooking ||
      !bookingAddressId ||
      !bookingSlotId ||
      selectedShipmentIds.size === 0 ||
      recovery.unavailable.length > 0
    ) {
      setPreview(null);
      return;
    }
    let current = true;
    const previewTimer = setTimeout(() => {
      previewMutation.mutate(
        {
          bookingId: booking.id,
          pickupAddressId: bookingAddressId,
          pickupServiceId: booking.pickupService.id,
          shipmentIds: Array.from(selectedShipmentIds),
          scheduledDate: booking.scheduledDate.split("T")[0],
          slotId: bookingSlotId ?? "",
        },
        {
          onSuccess: (data) => {
            if (current) {
              setPreview(data);
              setSubmitError(null);
            }
          },
          onError: (error) => {
            if (!current) return;
            setPreview(null);
            recovery.reportError(error);
            const code = isPickupBookingError(error)
              ? error.response?.data.error
              : undefined;
            if (code && TERMINAL_PICKUP_ERRORS.has(code)) setBlocked(true);
            setSubmitError(
              isPickupBookingError(error) && error.response?.data.message
                ? error.response.data.message
                : t("pickup.attach.addFailed")
            );
          },
        }
      );
    }, 250);
    return () => {
      current = false;
      clearTimeout(previewTimer);
    };
  }, [
    setPreview,
    open,
    targetUnavailable,
    blocked,
    refreshingBooking,
    t,
    selectedShipmentIds,
    bookingAddressId,
    booking.id,
    booking.pickupService.id,
    bookingSlotId,
    booking.scheduledDate,
    previewMutation.mutate,
    reviewRevision,
    recovery.reportError,
    recovery.unavailable.length,
  ]);

  const attachMutation = useAttachShipmentsToBooking();

  const handleSubmit = async () => {
    if (
      recovery.unavailable.length ||
      blocked ||
      targetUnavailable ||
      refreshingBooking ||
      bookingQuery.isFetching
    )
      return;
    if (
      !preview ||
      selectedShipmentIds.size === 0 ||
      !paymentReady ||
      paymentChoice === ""
    )
      return;
    setSubmitError(null);
    const identity = JSON.stringify({
      bookingId: booking.id,
      shipmentIds: [...selectedShipmentIds].sort(),
      paymentChoice,
      paymentMethodId,
      preview,
    });
    if (submission.current?.identity !== identity)
      submission.current = { identity, key: generateIdempotencyKey() };
    try {
      const result = await attachMutation.mutateAsync({
        bookingId: booking.id,
        expectedQuote: preview,
        shipmentIds: Array.from(selectedShipmentIds),
        paymentMethodType: paymentChoice,
        paymentMethodId:
          paymentChoice === "SAVED_CARD" ? paymentMethodId : null,
        idempotencyKey: submission.current.key,
      });
      toast({
        title: result.fromIdempotencyCache
          ? t("pickup.attach.alreadyAdded")
          : t("pickup.attach.addedToPickup", {
              count: result.attachedShipmentIds.length,
            }),
        description: preview
          ? t(
              paymentChoice === "INVOICE"
                ? "pickup.attach.addedToInvoice"
                : paymentChoice === "CREDIT"
                  ? "pickup.attach.deductedFromCredit"
                  : "pickup.attach.charged",
              {
                amount: numberFmt(
                  preview.totalCents + preview.taxCents,
                  preview.currency,
                  locale
                ),
              }
            )
          : undefined,
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      // Only a server-confirmed failed receipt permits a new provider attempt.
      // Keep the existing key for lost responses and other uncertain outcomes.
      if (
        isPickupBookingError(err) &&
        err.response?.data.error === "PAYMENT_FAILED" &&
        err.response.data.details?.paymentOutcome === "failed"
      ) {
        submission.current = null;
        setPreview(null);
        setReviewRevision((revision) => revision + 1);
      }

      if (recovery.reportError(err)) {
        setPreview(null);
        return;
      }
      const code =
        isPickupBookingError(err) && err.response?.data?.error
          ? err.response.data.error
          : null;
      if (code === "PREVIEW_CHANGED") {
        setPreview(null);
        setRefreshingBooking(true);
        try {
          const result = await bookingQuery.refetch();
          if (!result.error) setReviewRevision((revision) => revision + 1);
        } catch {
          setSubmitError(t("pickup.recovery.refreshFailed"));
        } finally {
          setRefreshingBooking(false);
        }
      }
      if (code && TERMINAL_PICKUP_ERRORS.has(code)) {
        setPreview(null);
        setBlocked(true);
      }
      const msg =
        isPickupBookingError(err) && err.response?.data?.message
          ? err.response.data.message
          : t("pickup.attach.addFailed");
      setSubmitError(msg);
    }
  };

  const toggle = (id: string) => {
    setSelectedShipmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const headerSubtitle = useMemo(() => {
    const dateLabel = new Date(booking.scheduledDate).toLocaleDateString(
      locale,
      { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" }
    );
    return `${dateLabel} · ${booking.slot ? formatSlotRange(booking.slot, locale) : t("pickup.timingUnknown")} · ${booking.pickupAddress.line1}, ${booking.pickupAddress.city}`;
  }, [booking, locale, t]);

  return (
    <Modal
      isOpen={open}
      onOpenChange={(v) => !v && onClose()}
      size="lg"
      scrollBehavior="inside"
      placement="center"
      classNames={{
        base: "m-0 sm:m-1 max-h-screen sm:max-h-[90vh] rounded-none sm:rounded-large",
      }}
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <div className="text-lg font-bold tracking-tight">
            {t("pickup.attach.modalTitle")}
          </div>
          <p className="text-tiny text-default-500 font-normal">
            {headerSubtitle}
          </p>
        </ModalHeader>

        <ModalBody className="p-0">
          {visitContact && (
            <SheetSection
              title={t("pickup.where.contactTitle")}
              hint={t("pickup.where.existingHint")}
            >
              <p className="text-sm">{visitContact.contactName}</p>
              {visitContact.contactCompany && (
                <p className="text-sm">{visitContact.contactCompany}</p>
              )}
              {visitContact.contactPhone && (
                <p className="text-sm">{visitContact.contactPhone}</p>
              )}
              {visitContact.contactEmail && (
                <p className="text-sm">{visitContact.contactEmail}</p>
              )}
              {visitContact.isResidential === true && (
                <p className="text-sm">{t("pickup.where.residential")}</p>
              )}
              {visitContact.instructions && (
                <p className="text-sm">{visitContact.instructions}</p>
              )}
              {booking.notes && <p className="text-sm">{booking.notes}</p>}
            </SheetSection>
          )}
          <PickupDeliveryConsequences
            consequences={preview?.deliveryConsequences}
            shipments={selectedShipments}
          />
          <PickupEligibilityNotice
            shipments={recovery.unavailable}
            disabled={attachMutation.isPending}
            onRemove={() => {
              recovery.removeUnavailable();
              setPreview(null);
              setSubmitError(null);
            }}
          />
          <SheetSection
            title={t("pickup.attach.pickTitle")}
            hint={t("pickup.attach.pickHint")}
          >
            {loadingShipments ? (
              <div className="flex items-center gap-2 text-sm text-default-500">
                <Spinner size="sm" color="primary" />
                {t("pickup.attach.loadingEligible")}
              </div>
            ) : visibleShipments.length === 0 ? (
              <div className="text-sm text-default-600">
                {t("pickup.attach.empty")}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {availableShipments.length === 0 && (
                  <p className="text-sm text-default-600">
                    {t("pickup.what.empty")}
                  </p>
                )}
                {availableShipments.length > 0 && (
                  <Button
                    size="sm"
                    variant="light"
                    onPress={() =>
                      setSelectedShipmentIds(
                        new Set([
                          ...selectedShipmentIds,
                          ...visibleShipments
                            .filter(
                              (s) => s.pickupEligibility?.eligible !== false
                            )
                            .map((s) => s.id),
                        ])
                      )
                    }
                  >
                    {t("pickup.what.selectAll")}
                  </Button>
                )}
                {availableShipments.map((s) => {
                  const checked = selectedShipmentIds.has(s.id);
                  const totalKg =
                    s.packages.reduce((a, p) => a + p.weightGrams, 0) / 1000;
                  return (
                    <SelectableCard
                      key={s.id}
                      selected={checked}
                      onSelect={() => toggle(s.id)}
                    >
                      <CheckBox selected={checked} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[13px] font-semibold">
                            SHP-{s.shipmentNumber}
                          </span>
                          <span className="text-[11px] text-default-500 truncate">
                            → {s.tasks[0]?.contactName ?? "—"}
                          </span>
                        </div>
                        <div className="text-[11.5px] text-default-500 mt-0.5">
                          {t("pickup.attach.packageCount", {
                            count: s.packages.length,
                          })}{" "}
                          · {totalKg.toFixed(1)} kg
                        </div>
                      </div>
                    </SelectableCard>
                  );
                })}
                <PickupUnavailableShipments
                  shipments={unavailableShipments}
                  selectedIds={selectedShipmentIds}
                />
              </div>
            )}
          </SheetSection>

          {selectedShipmentIds.size > 0 && (
            <SheetSection title={t("pickup.attach.pricePreview")}>
              <div className="rounded-medium bg-content1 ring-1 ring-inset ring-default-200 p-4 text-sm space-y-2">
                {previewMutation.isPending && !preview ? (
                  <div className="flex items-center gap-2 text-default-500">
                    <Spinner size="sm" color="primary" />
                    {t("pickup.attach.recalculating")}
                  </div>
                ) : previewMutation.isError && !submitError ? (
                  <p role="alert" className="text-tiny text-danger">
                    {isPickupBookingError(previewMutation.error) &&
                    previewMutation.error.response?.data?.error ===
                      "PACKAGE_WEIGHT_UNPRICED"
                      ? t("createShipment.pickupPackageWeightUnpriced")
                      : t("pickup.wizard.pricingFailedBody")}
                  </p>
                ) : preview ? (
                  <>
                    <div className="flex justify-between text-default-600">
                      <span>{t("pickup.attach.subtotal")}</span>
                      <span className="font-mono">
                        {numberFmt(
                          preview.rawTotalCents,
                          preview.currency,
                          locale
                        )}
                      </span>
                    </div>
                    {preview.bundleDiscountCents > 0 && (
                      <div className="flex justify-between text-success-700">
                        <span>
                          {t("pickup.attach.bundleDiscountAdded", {
                            count: selectedShipmentIds.size,
                          })}
                        </span>
                        <span className="font-mono">
                          −
                          {numberFmt(
                            preview.bundleDiscountCents,
                            preview.currency,
                            locale
                          )}
                        </span>
                      </div>
                    )}
                    {preview.taxCents > 0 && (
                      <div className="flex justify-between text-default-600">
                        <span>{t("pickup.common.hst")}</span>
                        <span className="font-mono">
                          {numberFmt(
                            preview.taxCents,
                            preview.currency,
                            locale
                          )}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold text-foreground border-t border-divider pt-2">
                      <span>
                        {paymentChoice === "INVOICE"
                          ? t("pickup.attach.addToInvoice")
                          : paymentChoice === "CREDIT"
                            ? t("pickup.attach.deductFromCredit")
                            : t("pickup.attach.chargeNow")}
                      </span>
                      <span className="font-mono font-bold">
                        {numberFmt(
                          preview.totalCents + preview.taxCents,
                          preview.currency,
                          locale
                        )}
                      </span>
                    </div>
                  </>
                ) : (
                  <p className="text-tiny text-default-500">
                    {t("pickup.attach.selectForPrice")}
                  </p>
                )}
              </div>
            </SheetSection>
          )}

          {selectedShipmentIds.size > 0 && (
            <SheetSection
              title={t("pickup.attach.payWith")}
              noDivider={!submitError}
            >
              <div className="flex flex-col gap-2">
                {paymentMethods.map((pm) => {
                  const isSelected =
                    paymentChoice === "SAVED_CARD" && paymentMethodId === pm.id;
                  return (
                    <SelectableCard
                      key={pm.id}
                      selected={isSelected}
                      onSelect={() => {
                        setPaymentChoice("SAVED_CARD");
                        setPaymentMethodId(pm.id);
                      }}
                    >
                      <RadioDot selected={isSelected} />
                      <CreditCard className="h-4 w-4 text-default-500 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-default-900">
                          {t("pickup.common.cardEnding", {
                            brand: pm.brand ?? t("pickup.common.card"),
                            last4: pm.last4 ?? "????",
                          })}
                        </div>
                        {pm.isDefault && (
                          <div className="text-[11.5px] text-default-500 mt-0.5">
                            {t("pickup.common.default")}
                          </div>
                        )}
                      </div>
                    </SelectableCard>
                  );
                })}
                <SelectableCard
                  selected={paymentChoice === "CREDIT"}
                  disabled={!canUseCredit}
                  onSelect={() => setPaymentChoice("CREDIT")}
                >
                  <RadioDot selected={paymentChoice === "CREDIT"} />
                  <Wallet className="h-4 w-4 text-success-600 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-default-900">
                      {t("pickup.confirm.accountCredit")}
                    </div>
                    <div className="text-[11.5px] text-default-500 mt-0.5">
                      {creditBalance?.frozen
                        ? t("pickup.confirm.creditFrozen")
                        : t("pickup.confirm.creditAvailable", {
                            amount: numberFmt(
                              creditBalance?.balanceCents ?? 0,
                              preview?.currency ?? "CAD",
                              locale
                            ),
                          })}
                    </div>
                  </div>
                </SelectableCard>
                <SelectableCard
                  selected={paymentChoice === "INVOICE"}
                  disabled={!canUseNetTerms}
                  onSelect={() => setPaymentChoice("INVOICE")}
                >
                  <RadioDot selected={paymentChoice === "INVOICE"} />
                  <FileText className="h-4 w-4 text-primary mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-default-900">
                      {t("pickup.confirm.netTerms")}
                    </div>
                    <div className="text-[11.5px] text-default-500 mt-0.5">
                      {canUseNetTerms
                        ? t("pickup.confirm.netTermsSource", {
                            terms:
                              selectedTermNames.join(", ") ||
                              t("pickup.confirm.netTerms"),
                          })
                        : (netTermsUnavailableReason ??
                          t("pickup.confirm.netTermsUnavailable"))}
                    </div>
                  </div>
                </SelectableCard>
                {paymentMethods.length === 0 &&
                  !canUseCredit &&
                  !canUseNetTerms && (
                    <p className="text-sm text-warning-700">
                      {t("pickup.confirm.noPaymentMethod")}
                    </p>
                  )}
              </div>
            </SheetSection>
          )}

          {(blocked || targetUnavailable) && onScheduleNew && (
            <SheetSection noDivider>
              <Button
                variant="bordered"
                onPress={() => onScheduleNew(Array.from(selectedShipmentIds))}
              >
                {t("pickup.recovery.newPickup")}
              </Button>
            </SheetSection>
          )}
          {targetUnavailable && !submitError && (
            <SheetSection noDivider>
              <p role="alert" className="text-sm text-danger">
                {t("pickup.recovery.targetUnavailable")}
              </p>
            </SheetSection>
          )}
          {submitError && (
            <SheetSection noDivider>
              <div
                role="alert"
                className="rounded-medium bg-danger-50 border border-danger-200 p-3 text-sm text-danger-700"
              >
                {submitError}
              </div>
              {!blocked && !preview && (
                <Button
                  className="mt-2"
                  variant="bordered"
                  isLoading={refreshingBooking}
                  onPress={async () => {
                    setRefreshingBooking(true);
                    try {
                      const result = await bookingQuery.refetch();
                      if (!result.error)
                        setReviewRevision((revision) => revision + 1);
                    } finally {
                      setRefreshingBooking(false);
                    }
                  }}
                >
                  {t("pickup.recovery.refreshReview")}
                </Button>
              )}
            </SheetSection>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="light" onPress={onClose}>
            {t("actions.cancel")}
          </Button>
          <Button
            color="primary"
            onPress={handleSubmit}
            isLoading={attachMutation.isPending}
            isDisabled={
              blocked ||
              targetUnavailable ||
              refreshingBooking ||
              bookingQuery.isFetching ||
              recovery.unavailable.length > 0 ||
              selectedShipmentIds.size === 0 ||
              !paymentReady ||
              !preview ||
              !bookingAddressId
            }
          >
            {attachMutation.isPending
              ? t("pickup.attach.adding")
              : t("pickup.attach.addButton", {
                  count: selectedShipmentIds.size,
                })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
